import React, { useEffect, useState } from "react";
import styled from "styled-components";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ===== API base (inline, per your pattern) ===== */
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE_URL) ||
  process.env.REACT_APP_API_BASE_URL;

/* Minimal HTTP helper */
async function http(path, { method = "GET", data, headers, signal } = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    body: data ? JSON.stringify(data) : undefined,
    signal,
    credentials: "include",
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.ok === false) {
    throw new Error(json?.error || `HTTP ${res.status}`);
  }
  return json;
}

/* Local API (page-only) */
const api = {
  listPublic: (params = {}) => {
    const usp = new URLSearchParams(params);
    const qs = usp.toString() ? `?${usp.toString()}` : "";
    return http(`/api/agent-posts/public${qs}`);
  },
};

/* ===== THEME / STYLES ===== */
const theme = {
  brand: "#3dccc7",
  brandDark: "#2a5a7a",
  accent: "#ff914d",
  text: "#0b1320",
  white: "#f1f1f1",
  subtext: "#6b7280",
  card: "#ffffff",
  border: "#e5e7eb",
  bg: "#f8fafc",
};

const PageShell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100dvh;
  background: ${theme.bg};
`;

/* Match your reference: adjust top padding at breakpoints */
const Main = styled.main`
  flex: 1 0 auto;
  padding-top: 80px;
  @media (max-width: 768px) {
    padding-top: 70px;
  }
  @media (max-width: 480px) {
    padding-top: 60px;
  }
`;

const Header = styled.section`
  background: linear-gradient(
    135deg,
    ${theme.brand} 0%,
    ${theme.brandDark} 100%
  );
  color: #fff;
  text-align: center;
  padding: clamp(2.4rem, 7vw, 3rem) 1rem;
`;
const H1 = styled.h1`
  margin: 0.75rem 0 0.6rem;
  font-weight: 900;
  letter-spacing: 0.2px;
  line-height: 1.12;
  font-size: clamp(1.8rem, 5.8vw, 3rem);
`;
const Sub = styled.p`
  margin: 0.85rem auto 0;
  opacity: 0.98;
  font-size: clamp(0.95rem, 2.4vw, 1.1rem);
  max-width: 880px;
`;

/* layout (wider desktop, comfy mobile) */
const Wrap = styled.div`
  display: grid;
  grid-template-columns: 340px 1fr;
  gap: 32px;
  max-width: min(1400px, 96vw);
  margin: 32px auto;
  padding: 0 16px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;
const Sidebar = styled.aside`
  position: sticky;
  top: 90px;
  align-self: start;
  display: grid;
  gap: 12px;

  @media (max-width: 980px) {
    position: static;
  }
`;

/* numbered list */
const Card = styled.div`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 16px;
  padding: 14px;
`;
const List = styled.ol`
  list-style: none;
  counter-reset: postcount;
  margin: 0;
  padding: 0;
  display: grid;
  gap: 6px;
`;
const ListItem = styled.li`
  counter-increment: postcount;
  display: grid;
  grid-template-columns: 28px 1fr;
  align-items: center;
  gap: 8px;
  a {
    display: block;
    padding: 8px 10px;
    border-radius: 10px;
    text-decoration: none;
    color: ${theme.text};
    border: 1px dashed transparent;
  }
  a:hover {
    background: #f3f4f6;
    border-color: ${theme.border};
  }
  &::before {
    content: counter(postcount) ".";
    font-weight: 800;
    color: ${theme.brandDark};
    text-align: right;
  }
`;

const Help = styled.div`
  margin-top: 12px;
  background: #0f1b2d;
  color: ${theme.white};
  border-radius: 14px;
  padding: 14px;
  border: 1px solid #1e293b;
  small {
    opacity: 0.85;
  }
`;

const Posts = styled.section`
  display: grid;
  gap: 32px;
  @media (max-width: 600px) {
    gap: 18px;
  }
`;
const Post = styled.article`
  background: ${theme.card};
  border: 1px solid ${theme.border};
  border-radius: 18px;
  padding: clamp(16px, 2vw, 24px);
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.06);
`;
const Title = styled.h2`
  margin: 0 0 6px;
  letter-spacing: 0.2px;
  font-size: clamp(1.1rem, 2.8vw, 1.4rem);
`;
const Meta = styled.div`
  font-size: 14px;
  color: ${theme.subtext};
`;
const Sep = styled.hr`
  border: 0;
  border-top: 1px dashed ${theme.border};
  margin: 16px 0;
`;
const Content = styled.div`
  line-height: 1.7;
  color: ${theme.text};
  text-align: justify;
  white-space: pre-wrap;
  font-size: clamp(0.92rem, 1.9vw, 0.95rem); /* smaller and responsive */
`;

const YTWrap = styled.div`
  position: relative;
  padding-top: 56.25%;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${theme.border};
  background: #000;
  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

/* Smaller Watch button + extra small on phones */
const WatchBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px; /* reduced size */
  border-radius: 999px;
  font-weight: 800;
  text-decoration: none;
  background: linear-gradient(45deg, ${theme.accent}, #ff6b35);
  color: #fff;
  box-shadow: 0 8px 22px rgba(255, 145, 77, 0.25);
  font-size: 0.9rem; /* slightly smaller */
  line-height: 1;

  &:hover {
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 7px 10px; /* even smaller on phones */
    font-size: 0.85rem;
  }
`;

export default function Agents() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try {
        setLoading(true);
        const res = await api.listPublic({});
        if (!ignore) setItems(res.data || []);
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  const fmt = (d) =>
    d
      ? new Date(d).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        })
      : "";
  const ytId = (url = "") => {
    try {
      if (!url) return "";
      const u = new URL(url);
      if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
      if (u.hostname.includes("youtube.com")) {
        if (u.searchParams.get("v")) return u.searchParams.get("v");
        const p = u.pathname.split("/").filter(Boolean);
        if (p[0] === "embed" && p[1]) return p[1];
      }
      return "";
    } catch {
      return "";
    }
  };
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const title = el.querySelector("h2") || el;
    let yOffset = -120; // desktop default
    if (window.innerWidth <= 480) yOffset = -80; // small screens
    else if (window.innerWidth <= 768) yOffset = -100; // tablets

    const y = title.getBoundingClientRect().top + window.scrollY + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  return (
    <PageShell>
      <Navbar />
      <Main>
        <Header>
          <H1>AI Agents - Guides & Walkthroughs</H1>
          <Sub>
            Practical notes on building and using AI Agents in construction
            workflows.
          </Sub>
        </Header>
        <Wrap>
          <Sidebar>
            <Card>
              <strong style={{ display: "block", marginBottom: 8 }}>
                All posts
              </strong>
              <List>
                {items.map((p) => (
                  <ListItem key={p._id}>
                    <a
                      href={`#${p.slug}`}
                      onClick={(e) => {
                        e.preventDefault();
                        scrollToId(p.slug);
                      }}
                    >
                      {p.title}
                    </a>
                  </ListItem>
                ))}
              </List>
            </Card>
            <Help>
              <strong>Want to create your own AI Agents?</strong>
              <div style={{ height: 8 }} />
              <small>
                Contact me at :{" "}
                <a
                  style={{ color: "#ff914d" }}
                  href="mailto:info@aibuildingskills.com"
                >
                  info@aibuildingskills.com
                </a>
              </small>
            </Help>
          </Sidebar>

          <Posts>
            {loading && <Card>Loading posts…</Card>}
            {!loading && items.length === 0 && <Card>No posts yet.</Card>}
            {!loading &&
              items.map((p) => (
                <Post id={p.slug} key={p._id}>
                  <Title>{p.title}</Title>
                  <Meta>By Abhishek Shete • Posted {fmt(p.postedAt)}</Meta>

                  {/* Watch Recording button (only if link exists) */}
                  {p.youtubeUrl ? (
                    <>
                      <div style={{ marginTop: 12 }}>
                        <WatchBtn
                          href={p.youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ▶ Watch Recording
                        </WatchBtn>
                      </div>
                      <Sep />
                    </>
                  ) : (
                    <Sep />
                  )}

                  {/* Embedded video (shows only if URL parses to an ID) */}
                  {p.youtubeUrl && ytId(p.youtubeUrl) && (
                    <>
                      <YTWrap>
                        <iframe
                          src={`https://www.youtube.com/embed/${ytId(
                            p.youtubeUrl
                          )}`}
                          title={p.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                        />
                      </YTWrap>
                      <Sep />
                    </>
                  )}

                  <Content>{p.content}</Content>
                </Post>
              ))}
          </Posts>
        </Wrap>
      </Main>
      <Footer />
    </PageShell>
  );
}
